import * as React from "react";
import { PromptGeneratorForm } from "@/components/feature/promptGenerator/form";
import { PromptGeneratorImprovedPrompt } from "@/components/feature/promptGenerator/improvedPrompt";
import { Loader } from "@/components/ui/loader";
import { ulid } from "ulid";
import { useGetMessagesQuery, useSendMessageMutation } from "@/api/open-chat";
import { OpenChatMessage } from "@/types/open-chat";
import { OpenChatWebSocketHandler } from "@/views/open-chat/components/webSocketHandler";

interface PromptGeneratorProps {
  contentRef: React.RefObject<HTMLDivElement>;
  isVisible: boolean;
  hideTitle?: boolean;
}
const emptyList: OpenChatMessage[] = [];

export const PromptGenerator: React.FC<PromptGeneratorProps> = ({ hideTitle, contentRef, isVisible }) => {
  const [connect, setConnect] = React.useState(false);
  const [conversationId] = React.useState(() => ulid());
  const { data: messages = emptyList, isLoading: isLoadingFirst } = useGetMessagesQuery(conversationId);
  const [sendMessageMutation, { isLoading }] = useSendMessageMutation();

  React.useEffect(() => {
    if (isVisible && !connect) {
      setConnect(true);
    }
  }, [isVisible, connect]);

  const scrollDown = React.useCallback(() => {
    if (contentRef.current !== null && isVisible) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
    // contentRef is not a dependency because it is not changing (ref)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const handleImprovePrompt = React.useCallback(
    async (message: string) => {
      await sendMessageMutation({
        conversationId,
        message,
        messageId: ulid(),
        prompt: "open-chat-prompt-generator",
      }).unwrap();

      scrollDown();
    },
    [scrollDown, conversationId, sendMessageMutation],
  );

  if (!isVisible) {
    return null;
  }

  return (
    <div className="p-4">
      {!hideTitle && <h2 className="mb-4 text-lg font-bold">Prompt Generator</h2>}
      <OpenChatWebSocketHandler contentRef={contentRef} conversationId={conversationId} />

      <div>
        {messages.map((improvedPrompt, index) => (
          <PromptGeneratorImprovedPrompt
            key={index}
            improvedPrompt={improvedPrompt.content}
            isWorking={!improvedPrompt.completed}
          />
        ))}
        {(isLoadingFirst || isLoading) && <Loader />}
        <PromptGeneratorForm handleImprovePrompt={handleImprovePrompt} isDisabled={isLoading || isLoadingFirst} />
      </div>
    </div>
  );
};
